/**
 * PostcardCard — Sprint 4A (visual spec aligned with losdias.xyz in 4C)
 * Renders a city postcard EXACTLY like the losdias.xyz checkout card so the app
 * and the web match: a 3:2 white frame, the city illustration shown whole
 * (object-fit: contain, anchored to the bottom), and overlaid text top-left —
 * city in Fraunces italic terracotta, country in Manrope uppercase navy.
 *
 * Art comes from the app's TRIP/img (resolved from country+city, or a given
 * relative `img`). If the image is missing/fails, a quiet cream fallback shows
 * (never a broken image). Fonts (Fraunces + Manrope) are loaded in index.html.
 */
import { useState, useEffect } from 'react';
import { resolvePostcardArtPath, postcardArtUrl } from '../utils/postcardManager';

// Brand palette (Los Días)
const TERRA = '#C4714A';
const NAVY = '#1A3340';
const CREAM = '#f5f1eb';

// "iceland" → "Iceland", "czech-republic" → "Czech Republic"
function countryFromRel(rel) {
  if (!rel) return '';
  const folder = String(rel).split('/')[0] || '';
  return folder.split('-').map(w => w ? w[0].toUpperCase() + w.slice(1) : w).join(' ');
}

export default function PostcardCard({ city, country, img }) {
  const [rel, setRel] = useState(img || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (img) { setRel(img); return; }
    setRel(null);
    resolvePostcardArtPath(country, city).then(p => { if (!cancelled) setRel(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [img, country, city]);

  const src = postcardArtUrl(rel);
  const countryLabel = country || countryFromRel(rel);
  const showFallback = !src || failed;

  return (
    <div style={{ ...S.frame, background: showFallback ? CREAM : '#ffffff' }}>
      {!showFallback && (
        <img src={src} alt={city} onError={() => setFailed(true)} style={S.img} />
      )}

      {showFallback ? (
        <div style={S.fallback}>
          <span style={S.fbLabel}>Postal</span>
          <span style={S.fbCity}>{city}</span>
          <span style={S.fbRule} />
        </div>
      ) : (
        <div style={S.text}>
          <span style={S.city}>{city}</span>
          {countryLabel && <span style={S.country}>{countryLabel}</span>}
        </div>
      )}
    </div>
  );
}

const S = {
  frame: {
    position: 'relative',
    width: '100%',
    aspectRatio: '3 / 2',
    borderRadius: '2px',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1)',
    outline: '1px solid rgba(0,0,0,.1)',
    outlineOffset: '-1px',
  },
  img: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'bottom',
    display: 'block',
  },
  text: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: '16px 0 0 20px',
    textAlign: 'left',
    pointerEvents: 'none',
  },
  city: {
    display: 'block',
    fontFamily: "'Fraunces', Georgia, serif",
    fontStyle: 'italic',
    fontWeight: 500,
    color: TERRA,
    fontSize: 'clamp(1.1rem, 3.5vw, 1.85rem)',
    lineHeight: 1,
  },
  country: {
    display: 'block',
    marginTop: '4px',
    fontFamily: "'Manrope', system-ui, sans-serif",
    fontWeight: 500,
    textTransform: 'uppercase',
    color: NAVY,
    fontSize: 'clamp(0.5rem, 1.5vw, 0.7rem)',
    letterSpacing: '0.22em',
  },
  fallback: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 16px',
  },
  fbLabel: {
    fontFamily: "'Manrope', system-ui, sans-serif",
    fontSize: '0.6rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.35em',
    color: TERRA,
    marginBottom: '10px',
  },
  fbCity: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontWeight: 700,
    color: NAVY,
    fontSize: 'clamp(1.75rem, 6vw, 3rem)',
    lineHeight: 1.05,
  },
  fbRule: {
    display: 'block',
    width: '48px',
    height: '3px',
    background: TERRA,
    borderRadius: '2px',
    marginTop: '14px',
  },
};
